export default function PrivacyPolicy() {
  return (
    <>
      <div className="relative overflow-hidden">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-[37.5rem] pt-20 text-center pb-24">
            <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Privacy Policy
            </h1>
            <p className="mt-4 text-base leading-7 text-slate-600">
              Effective Date: Jan 1, 2023
            </p>
          </div>
        </div>
        <div className="relative px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-[40rem] prose-sm prose prose-slate prose-a:font-semibold prose-a:text-sky-500 hover:prose-a:text-sky-600">
            <p>
              Elmbase, Inc. and its affiliates (“Elmbase,” “we,” “our,” and/or
              “us”) value the privacy of individuals who use our website and
              related services (collectively, our “Services”). This privacy
              policy (the “Privacy Policy”) explains how we collect, use, and
              share information from users of our Services (“Users”). By using
              our Services, you agree to the collection, use, disclosure, and
              procedures this Privacy Policy describes. Beyond the Privacy
              Policy, your use of our Services is also subject to our Terms of
              Service.
            </p>
            <h2>Information We Collect</h2>
            <p>
              We may collect a variety of information from or about you or your
              devices from various sources, as described below.
            </p>
            <h2>Information You Provide to Us</h2>
            <p>
              Registration. When you register to use our Services, we ask you
              for your name, email address, phone number, username/password, and
              accreditation status. We may also ask for documentation to verify
              your accreditation status.
            </p>
            <p>
              Profile Information. Once registered to use our Services, we
              collect additional information from you to create your profile.
              For example, we may ask you for your phone number, company and
              company address, job title, industry, date of birth, and an image
              for your profile picture.{" "}
            </p>
            <p>
              Communications. If you contact us directly, we may receive
              additional information about you. For example, when you contact
              our customer support team, we will receive your name, email
              address, the contents of a message or attachments that you may
              send to us, and other information you choose to provide. If you
              participate in one of our surveys, we may receive information
              about you relating to the subject matter of the survey. If you
              subscribe to our newsletter, then we will collect certain
              information from you, such as your email address. When we send you
              emails, we may track whether you open them to learn how to deliver
              a better customer experience and improve our Services.
            </p>
            <h2>Information We Collect When You Use Our Services</h2>
            <p>
              Device Information. We receive information about the device and
              software you use to access our Services, including IP address
              (which may provide us with your general location information), web
              browser type, operating system version, phone carrier and
              manufacturer, application installations, device identifiers,
              mobile advertising identifiers, and push notification tokens.
            </p>
            <p>
              Usage Information. To help us understand how you use our Services
              and to help us improve them, we automatically receive information
              about your interactions with our Services, like the pages or other
              content you view, your browsing history, the searches you conduct,
              people you follow, investments you make, your comments, any
              content you post, clickstream data, advertising interactions, and
              the dates and times of your visits.{" "}
            </p>
            <p>
              Information from Cookies and Similar Technologies. We and our
              third-party partners collect information using cookies, pixel
              tags, or similar technologies. Our third-party partners, such as
              analytics and advertising partners, may use these technologies to
              collect information about your online activities over time and
              across different services. Cookies are small text files containing
              a string of alphanumeric characters. We may use both session
              cookies and persistent cookies. A session cookie disappears after
              you close your browser. A persistent cookie remains after you
              close your browser and may be used by your browser on subsequent
              visits to our Services. Please review your web browser’s “Help”
              file to learn the proper way to modify your cookie settings.
              Please note that if you delete or choose not to accept cookies
              from the Service, you may not be able to utilize the features of
              the Service to their fullest potential.
            </p>
            <h2>Information We Receive from Third Parties</h2>
            <p>
              Credit Reporting Agencies. If you are a sponsor of an offering or
              register for an investment account through our Services, we may
              receive information about you from credit reporting agencies to
              comply with applicable laws and industry standards, such as Know
              Your Business or Know Your Customer (KYC) and Anti-Money
              Laundering (AML). For example, we may receive your pass/fail
              status of KYC and AML. We may also receive information from credit
              reporting agencies to verify your bank accounts and other
              ownership information.{" "}
            </p>
            <p>
              Registration Information. You can log in to the Services using
              third party credentials such as LinkedIn or Google. These services
              will authenticate your identity and provide you the option to
              share certain personal information with us, such as your name and
              email address to pre-populate our registration form. Other third
              parties. We may receive additional information about you from
              third parties such as data or marketing partners and combine it
              with other information we have about you. For example, we may
              receive your name, e-mail address, and general location
              information derived from your IP address from marketing partners
              to learn more about demand for current and prospective users.
            </p>
            <h2>How We Use the Information We Collect</h2>
            <p>We use the information we collect:</p>
            <ul>
              <li>To provide, maintain, improve, and enhance our Services;</li>
              <li>
                To personalize your experience on our Services such as by
                providing tailored content and recommendations;{" "}
              </li>
              <li>
                To understand and analyze how you use our Services and develop
                new products, services, features, and functionality;
              </li>
              <li>
                To communicate with you, provide you with updates and other
                information relating to our Services, provide information that
                you request, respond to comments and questions, and otherwise
                provide customer support;
              </li>
              <li>
                To provide you with investment opportunities to match your
                individual investment goals;
              </li>
              <li>
                To facilitate offers on behalf of other Users or third parties.{" "}
              </li>
              <li>
                For marketing and advertising purposes, such as developing and
                providing promotional and advertising materials that may be
                relevant, valuable or otherwise of interest to you;
              </li>
              <li>
                To generate anonymized, aggregate data containing only
                de-identified, non-personal information that we may use to
                publish reports;
              </li>
              <li>To send you text messages and push notifications;</li>
              <li>To facilitate transactions and payments;</li>
              <li>
                To find and prevent fraud, and respond to trust and safety
                issues that may arise;{" "}
              </li>
              <li>
                For compliance purposes, including enforcing our Terms of Use or
                other legal rights, or as may be required by applicable laws and
                regulations or requested by any judicial process or governmental
                agency; and
              </li>
              <li>
                For other purposes for which we provide specific notice at the
                time the information is collected.
              </li>
            </ul>
            <h2>How We Share the Information We Collect</h2>
            <p>
              Vendors and Service Providers. We may share any information we
              receive with vendors and service providers retained in connection
              with the provision of our Services.{" "}
            </p>
            <p>
              User Content. Our Services allow Users to share certain
              information with other Users of the Services. We are not
              responsible for the other users’ use of available information, so
              you should carefully consider whether and what to post or how you
              identify yourself on the Services.{" "}
            </p>
            <p>
              Investment Sponsors. If you indicate an interest in making an
              investment through the Services, we will notify the party or
              parties offering the investment and their affiliates (the
              “Issuer”) of your interest and may notify other Users with whom
              you have chosen to share information. Note that, upon your
              admission to an investment offering (a "Company"), the Issuer may
              share information including, but not limited to, member names,
              initial capital contributions, and distribution amounts with other
              members of the Company. Please refer to the terms of the Company's
              operating agreement and relevant state law for additional detail.
              Social Networks and Other Online Services. Our Services allow you
              to, upon your direction, share information with social networking
              services, such as Twitter, Facebook and Instagram. You understand
              and agree that the use of your information by any social
              networking websites will be governed by the privacy policies of
              these third-party platforms and your settings on that platform. We
              encourage you to review their privacy policies.{" "}
            </p>
            <p>
              Marketing. We do not rent, sell, or share information about you
              with non affiliated companies for their direct marketing purposes,
              unless we have your permission. Analytics Partners. We use
              analytics services such as Google Analytics to collect and process
              certain analytics data. These services may also collect
              information about your use of other websites, apps, and online
              resources. You can learn more about Google’s practices by visiting
              Google Privacy & Terms. To help us understand how you use our
              Services and to help us improve them, we automatically receive
              information about your interactions with our Services, like the
              pages or other content you view, your browsing history, the
              searches you conduct, people you follow, investments you make,
              your comments, any content you post, clickstream data, advertising
              interactions, and the dates and times of your visits.{" "}
            </p>
            <p>
              Advertising Partners. We may work with third-party advertising
              partners to show you ads that we think may interest you. You can
              access any settings offered by your mobile operating system to
              limit ad tracking. As Required By Law and Similar Disclosures. We
              may access, preserve, and disclose your information if we believe
              doing so is required or appropriate to: (a) comply with law
              enforcement requests and legal process, such as a court order or
              subpoena; (b) respond to your requests; or (c) protect your, our,
              or others’ rights, property, or safety. For the avoidance of
              doubt, the disclosure of your information may occur if you post
              any objectionable content on or through the Services.{" "}
            </p>
            <p>
              Merger, Sale, or Other Asset Transfers. We may transfer your
              information to service providers, advisors, potential
              transactional partners, or other third parties in connection with
              the consideration, negotiation, or completion of a corporate
              transaction in which we are acquired by or merged with another
              company or we sell, liquidate, or transfer all or a portion of our
              assets. The use of your information following any of these events
              will be governed by the provisions of this Privacy Policy in
              effect at the time the applicable information was collected.
            </p>
            <p>
              Consent. We may also disclose your information with your
              permission.
            </p>
            <h2>Your Choices</h2>
            <p>
              Sharing Preferences. We may provide you with settings to allow you
              to set your profile and other sharing preferences on the Services.
              Certain information may always be publicly available to others,
              and other information is made publicly available to others by
              default.{" "}
            </p>
            <p>
              Marketing Communications. You can unsubscribe from our promotional
              emails via the link provided in the emails. Even if you opt-out of
              receiving promotional messages from us, you will continue to
              receive administrative messages from us. Do Not Track. There is no
              accepted standard on how to respond to Do Not Track signals, and
              we do not respond to such signals.{" "}
            </p>
            <p>
              Third Parties: Our Services may contain links to other websites,
              products, or services that we do not own or operate. We are not
              responsible for the privacy practices of these third parties.
              Please be aware that this Privacy Policy does not apply to your
              activities on these third-party services or any information you
              disclose to these third parties. We encourage you to read their
              privacy policies before providing any information to them.
            </p>
            <h2>Data Storage</h2>
            <p>
              All user data is stored on secure servers located in a data center
              that is compliant with industry-standard security protocols. The
              servers are protected by firewalls, intrusion detection systems,
              and other security measures to prevent unauthorized access.
            </p>
            <h2>Data Security</h2>
            <p>
              All user data is encrypted in transit and at rest to protect
              against unauthorized access or alteration. We use
              industry-standard encryption algorithms to ensure that data is
              protected. Access to user data is restricted to authorized
              personnel only. Access to user data is logged and monitored to
              detect any unauthorized access or attempted breaches. We regularly
              backup all user data to ensure that it is protected in the event
              of a system failure or other disaster. Backups are stored in a
              secure location and are encrypted to protect against unauthorized
              access. We retain user data only as long as necessary to provide
              the services offered on our platform. Once a user account is
              deleted, all data associated with that account is also deleted. We
              make reasonable efforts to protect your information by using
              physical and electronic safeguards designed to improve the
              security of the information we maintain. However, as no electronic
              transmission or storage of information can be entirely secure, we
              can make no guarantees as to the security or privacy of your
              information.
            </p>
            <h2>Children’s Privacy</h2>
            <p>
              We do not knowingly collect, maintain, or use personal information
              from children under 13 years of age, and no part of our Service(s)
              is directed to children. If you learn that a child has provided us
              with personal information in violation of this Privacy Policy,
              then you may alert us at privacy@elmbase.com.
            </p>
            <h2>Updating Your Information</h2>
            <p>
              You can update your account and profile information or close your
              account through your profile settings.
            </p>
            <h2>Changes to this Privacy Policy</h2>
            <p>
              We will post any adjustments to the Privacy Policy on this page,
              and the revised version will be effective when it is posted. If we
              materially change the ways in which we use or share personal
              information previously collected from you through the Services, we
              will notify you through the Services, by email, or other
              communication.
            </p>
            <h2>Contact Information</h2>
            <p className="pb-16">
              If you have any questions, comments, or concerns about our
              processing activities, please email us at privacy@elmbase.com.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
